// frontend/src/components/settings/ExactDisconnectDialog.jsx
//
// Confirmation dialog for disconnecting an Exact ERP connector. Disconnect is
// destructive and irreversible: ERP-only items/locations are deleted, survivors
// (also sold on a sales channel) keep their data but lose the ERP link, and
// suppliers are unlinked. Rows that anchor real sales / PO / stock history are
// NOT deleted — they're kept and flagged in Data Health ("left over from
// Exact") for the merchant to retire.
//
// The preview is computed live by the backend (no mutation) so the merchant
// sees the exact impact before confirming.

import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import DialogErrorBanner from '../common/DialogErrorBanner';

import {
  useExactDisconnectPreview,
  useDisconnectExact,
} from '../../hooks/useConnectorMutations';
import { translateRequestError } from '../../queries/api';

// Summary line: a count + its label, rendered only when the count is non-zero
// so the dialog shows just the consequences that actually apply.
function ImpactLine({ count, label }) {
  if (!count) return null;
  return (
    <li>
      <strong>{count}</strong> {label}
    </li>
  );
}

export default function ExactDisconnectDialog({ connectorId, open, onClose, onConfirmed }) {
  const { t } = useTranslation();
  const { data, isPending, error } = useExactDisconnectPreview(connectorId, open);
  const disconnectMut = useDisconnectExact(connectorId);

  const handleConfirm = async () => {
    try {
      const result = await disconnectMut.mutateAsync();
      onConfirmed?.(result);
      onClose?.();
    } catch {
      // Error surfaced inline below via disconnectMut.error.
    }
  };

  const nothingToDelete =
    !isPending && !error
    && !(data.items_deleted || data.locations_deleted);

  return (
    <Dialog className="dialog-form dialog-form--confirm" open={open} onClose={onClose}>
      <DialogTitle>{t('connectors.disconnectTitle')}</DialogTitle>
      <DialogContent>
        {isPending ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <CircularProgress size={24} />
          </div>
        ) : error ? (
          <Alert severity="error">{t('connectors.disconnectPreviewFailed')}</Alert>
        ) : (
          <Stack spacing={2}>
            <Alert severity="warning">{t('connectors.disconnectWarning')}</Alert>

            <div>
              <p style={{ margin: '0 0 8px', fontWeight: 'var(--font-weight-semibold)' }}>
                {t('connectors.disconnectImpactHeading')}
              </p>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <ImpactLine count={data.items_deleted} label={t('connectors.disconnectItemsDeleted')} />
                <ImpactLine count={data.locations_deleted} label={t('connectors.disconnectLocationsDeleted')} />
                <ImpactLine count={data.items_unlinked} label={t('connectors.disconnectItemsUnlinked')} />
                <ImpactLine count={data.locations_unlinked} label={t('connectors.disconnectLocationsUnlinked')} />
                <ImpactLine count={data.suppliers_unlinked} label={t('connectors.disconnectSuppliersUnlinked')} />
                <ImpactLine count={data.pos_unlinked} label={t('connectors.disconnectPosUnlinked')} />
                {nothingToDelete && (
                  <li>{t('connectors.disconnectNothingDeleted')}</li>
                )}
              </ul>
            </div>

            {(data.items_flagged || data.locations_flagged) ? (
              <Alert severity="info">
                {t('connectors.disconnectFlagged', {
                  count: data.items_flagged + data.locations_flagged,
                })}
              </Alert>
            ) : null}
          </Stack>
        )}
        <DialogErrorBanner
          error={disconnectMut.error && translateRequestError(disconnectMut.error, t, 'connectors.disconnectFailed')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={disconnectMut.isPending}>
          {t('btn.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={isPending || !!error}
          loading={disconnectMut.isPending}
        >
          {t('connectors.disconnectConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
